<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import type { Ref } from 'vue'
import BaseIcon from '@/components/BaseIcon.vue'
import { FORM_FIELD_LABEL_KEY } from '@/components/formFieldContext'

type InputSize = 'sm' | 'md' | 'lg' | 'filter'
type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search' | 'datetime-local' | 'date' | 'file'
type InputVariant = 'default' | 'outlined' | 'compact'

const props = withDefaults(defineProps<{
  modelValue?: string | number
  type?: InputType
  placeholder?: string
  disabled?: boolean
  readonly?: boolean
  error?: boolean
  errorMessage?: string
  size?: InputSize
  variant?: InputVariant
  clearable?: boolean
  /** 密码框右侧显示小眼睛；仅 type=password 时生效，默认开启 */
  passwordToggle?: boolean
  maxlength?: number
  autofocus?: boolean
  textarea?: boolean
  rows?: number
  /** 透传到内部 input/textarea 的 autocomplete；外层组件根节点是 div，attr 透传不会落到输入框上 */
  autocomplete?: string
  name?: string
  /** 显式无障碍标签；未传时兜底用外层 NextFormField 的字段 label（注入），再无则不输出 */
  ariaLabel?: string
}>(), {
  modelValue: '',
  type: 'text',
  placeholder: '',
  disabled: false,
  readonly: false,
  error: false,
  errorMessage: '',
  size: 'md',
  variant: 'default',
  clearable: false,
  passwordToggle: true,
  maxlength: undefined,
  autofocus: false,
  textarea: false,
  rows: 3,
  autocomplete: undefined,
  name: undefined,
  ariaLabel: undefined
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  'clear': []
  'focus': [event: FocusEvent]
  'blur': [event: FocusEvent]
}>()

const passwordVisible = ref(false)

/** 无障碍标签：显式 ariaLabel > 外层 NextFormField 注入的字段 label > 不输出 */
const fieldLabelInjected = inject<Ref<string> | null>(FORM_FIELD_LABEL_KEY, null)
const resolvedAriaLabel = computed(() => props.ariaLabel || fieldLabelInjected?.value || undefined)

const showPasswordToggle = computed(
  () => props.type === 'password' && props.passwordToggle && !props.textarea
)

const resolvedType = computed(() => {
  if (showPasswordToggle.value && passwordVisible.value) {
    return 'text'
  }
  return props.type
})

const showClear = computed(() => Boolean(props.clearable && props.modelValue && !props.disabled))

const inputClass = computed(() => [
  'base-input',
  `base-input--${props.size}`,
  `base-input--${props.variant}`,
  {
    'base-input--error': props.error,
    'base-input--disabled': props.disabled,
    'base-input--clearable': showClear.value,
    'base-input--password-toggle': showPasswordToggle.value,
    'base-input--is-textarea': props.textarea
  }
])

function handleInput(event: Event): void {
  const target = event.target as HTMLInputElement | HTMLTextAreaElement
  emit('update:modelValue', target.value)
}

function handleClear(): void {
  emit('update:modelValue', '')
  emit('clear')
}

function handleFocus(event: FocusEvent): void {
  emit('focus', event)
}

function handleBlur(event: FocusEvent): void {
  emit('blur', event)
}

function togglePasswordVisibility(): void {
  passwordVisible.value = !passwordVisible.value
}
</script>

<template>
  <div class="base-input-wrapper">
    <div class="base-input__field">
      <textarea
        v-if="textarea"
        :class="inputClass"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :rows="rows"
        :autofocus="autofocus"
        :autocomplete="autocomplete"
        :name="name"
        :aria-label="resolvedAriaLabel"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <input
        v-else
        :class="inputClass"
        :type="resolvedType"
        :value="modelValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxlength"
        :autofocus="autofocus"
        :autocomplete="autocomplete"
        :name="name"
        :aria-label="resolvedAriaLabel"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <button
        v-if="showClear"
        type="button"
        class="base-input__clear"
        :class="{ 'base-input__clear--with-password-toggle': showPasswordToggle }"
        aria-label="清空"
        @click="handleClear"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
      <button
        v-if="showPasswordToggle"
        type="button"
        class="base-input__password-toggle"
        :disabled="disabled"
        :title="passwordVisible ? '隐藏密码' : '显示密码'"
        :aria-label="passwordVisible ? '隐藏密码' : '显示密码'"
        :aria-pressed="passwordVisible"
        @click="togglePasswordVisibility"
      >
        <BaseIcon :name="passwordVisible ? 'eye-off' : 'eye'" :size="16" />
      </button>
    </div>
    <span v-if="error && errorMessage" class="base-input__error-message">
      {{ errorMessage }}
    </span>
  </div>
</template>

<style lang="scss" scoped>
.base-input-wrapper {
  display: inline-flex;
  flex-direction: column;
  width: 100%;
}

.base-input__field {
  position: relative;
  width: 100%;
}

.base-input {
  width: 100%;
  color: var(--text-primary);
  font-size: var(--text-sm);
  font-family: inherit;
  outline: none;
  transition: all var(--transition-fast);
  box-sizing: border-box;

  // textarea 模式：不固定高度，自动撑开
  &.base-input--is-textarea {
    height: auto;
    min-height: 80px;
    resize: vertical;
    line-height: var(--line-height-normal);
  }

  &--sm {
    padding: var(--spacing-2) var(--spacing-3);
    font-size: var(--text-xs);

    &.base-input--password-toggle {
      padding-right: var(--spacing-10);
    }

    &.base-input--clearable.base-input--password-toggle {
      padding-right: calc(var(--spacing-10) + var(--spacing-8));
    }
  }

  &--md {
    padding: var(--spacing-3) var(--spacing-4);
    /* /next → 36；旧版 /app 无令牌 → 48 */
    height: var(--nx-control-h-md, 48px);

    &.base-input--is-textarea {
      height: auto;
    }
  }

  &--lg {
    padding: var(--spacing-3) var(--spacing-5);
    font-size: var(--text-base);
    height: 56px;

    &.base-input--is-textarea {
      height: auto;
    }
  }

  // 筛选行/工具栏档：对齐新版规格令牌 --nx-control-h-sm（规格册 2.5，唯一出处）
  // 回退 36px：--nx-* 令牌仅覆盖 .next-shell/.modal-overlay，旧版场景用回退值
  &--filter {
    height: var(--nx-control-h-sm, 32px);
    padding: 0 var(--spacing-3);

    &.base-input--password-toggle {
      padding-right: var(--spacing-10);
    }

    &.base-input--clearable.base-input--password-toggle {
      padding-right: calc(var(--spacing-10) + var(--spacing-8));
    }
  }

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &--clearable {
    padding-right: var(--spacing-12);
  }

  &--password-toggle {
    padding-right: var(--spacing-12);
  }

  &--clearable.base-input--password-toggle {
    padding-right: calc(var(--spacing-12) + var(--spacing-8));
  }

  // Variant: default（/app：无框，靠底色 + 阴影；/next：由 --nx-field-* 令牌接管为白底 + 1px 描边）
  &--default {
    background: var(--nx-field-bg, var(--surface));
    border: 1px solid var(--nx-field-border, transparent);
    border-radius: var(--nx-field-radius, var(--radius-lg));
    box-shadow: var(--shadow-input);

    &:hover:not(:disabled):not(:focus) {
      background: var(--nx-field-bg-hover, var(--surface-dim));
      border-color: var(--nx-field-border-hover, transparent);
    }

    &:focus {
      background: var(--white);
      border-color: var(--nx-field-border-focus, var(--primary-300));
      box-shadow: var(--nx-field-shadow-focus, var(--shadow-input), 0 0 0 3px var(--primary-100));
    }

    &:disabled {
      background: var(--surface);
    }
  }

  // Variant: outlined (bordered, surface-bright background)
  &--outlined {
    background: var(--surface-bright);
    border: var(--input-border-width) solid var(--border-color);
    border-radius: var(--input-radius);
    box-shadow: var(--shadow-input);

    &:hover:not(:disabled):not(:focus) {
      border-color: var(--primary-200);
    }

    &:focus {
      background: var(--surface-bright);
      border-color: var(--primary);
      box-shadow: var(--shadow-input), 0 0 0 3px var(--primary-100), var(--shadow-glow);
    }

    &:disabled {
      background: var(--surface-dim);
    }
  }

  // Variant: compact (small radius, compact)
  &--compact {
    padding: var(--spacing-2) var(--spacing-3);
    background: var(--surface-bright);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-sm);
    box-shadow: var(--shadow-input);
    font-size: var(--text-xs);

    &:focus {
      border-color: var(--primary);
      background: var(--surface-bright);
    }

    &:disabled {
      background: var(--surface-dim);
    }
  }

  // 错误态放在各变体之后：错误描边/底色必须盖过变体的常规描边
  &--error {
    border-color: var(--danger);
    background: var(--danger-50);

    &:focus {
      box-shadow: var(--shadow-input), 0 0 0 4px var(--danger-alpha-10);
    }
  }
}

.base-input__clear {
  position: absolute;
  right: var(--spacing-2);
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);

  &--with-password-toggle {
    right: calc(var(--spacing-2) + 28px);
  }

  &:hover {
    color: var(--text-primary);
    background: var(--bg-tertiary);
  }
}

.base-input__password-toggle {
  position: absolute;
  right: var(--spacing-2);
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);
  z-index: 1;

  &:hover:not(:disabled) {
    color: var(--primary);
    background: var(--primary-50);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.base-input__error-message {
  font-size: var(--text-xs);
  color: var(--danger);
  margin-top: var(--spacing-1);
}
</style>
